import { call, put, takeLatest } from "redux-saga/effects";
import { manageApplicationSagaActionTypes } from "./saga-actions";
import { manageApplicationSliceAction } from "../../slices/manage-application";
import {
  ManageApplicationPayload,
  CheckApplicationResponse,
  DeleteApplicationPayload,
} from "../../slices/manage-application/Types";
import { AxiosResponse } from "axios";
import { axiosRestInstance } from "../../../common/axios";
import { getOnboardingDetailListSagaAction } from "../onboarding-form/saga-actions";

type manageApplicationAPIResponseType = {
  success: boolean;
  message: string;
};

const manageApplicationAPI = async (payload: ManageApplicationPayload) => {
  const response = await axiosRestInstance
    .put<AxiosResponse<manageApplicationAPIResponseType>>(
      `/onboarding/application/${payload.application_id}`,
      payload
    )
    .then((res) => res.data);
  return response;
};

const checkApplicationIDAPI = async (applicationId: string) => {
  const response = await axiosRestInstance
    .get<AxiosResponse<CheckApplicationResponse>>(
      `/checkApplication?applicationId=${applicationId}`
    )
    .then((res) => res.data);
  return response;
};

type DeleteApplicationResponse = {
  success: boolean;
  message: string;
};

const deleteApplicationIDAPI = async (payload: DeleteApplicationPayload) => {
  const response = await axiosRestInstance
    .put<AxiosResponse<DeleteApplicationResponse>>(
      `/onboarding/deleteAppIDs/${payload.application_id}`,
      payload
    )
    .then((res) => res.data);
  return response;
};

// API Above --- Saga Below

function* manageApplicationSaga(action: {
  type: string;
  payload: ManageApplicationPayload;
}) {
  try {
    yield put(manageApplicationSliceAction.setManageApplicationLoading(true));
    yield put(manageApplicationSliceAction.setManageApplicationStatus(false));
    yield put(manageApplicationSliceAction.setManageApplicationMessage(""));

    const response: manageApplicationAPIResponseType = yield call(
      manageApplicationAPI,
      action.payload
    );

    yield put(manageApplicationSliceAction.setManageApplicationStatus(true));
    yield put(
      manageApplicationSliceAction.setManageApplicationMessage(response.message)
    );
    yield put(getOnboardingDetailListSagaAction(action.payload.partner_id));
    yield put(manageApplicationSliceAction.setCheckApplicationID(null));
  } catch (error: any) {
    yield put(manageApplicationSliceAction.setManageApplicationStatus(false));
    yield put(
      manageApplicationSliceAction.setManageApplicationMessage(
        error?.response?.data?.message || "An error occurred"
      )
    );
  } finally {
    yield put(manageApplicationSliceAction.setManageApplicationLoading(false));
  }
}

function* checkApplicationIDSaga(action: { type: string; payload: string }) {
  try {
    yield put(manageApplicationSliceAction.setCheckApplicationLoading(true));
    yield put(manageApplicationSliceAction.setCheckApplicationStatus(false));
    yield put(manageApplicationSliceAction.setCheckApplicationMessage(""));

    const response: CheckApplicationResponse = yield call(
      checkApplicationIDAPI,
      action.payload
    );
    yield put(
      manageApplicationSliceAction.setCheckApplicationID(action.payload)
    );
    yield put(
      manageApplicationSliceAction.setCheckApplicationResponse(response)
    );
    yield put(manageApplicationSliceAction.setCheckApplicationStatus(true));
    yield put(
      manageApplicationSliceAction.setCheckApplicationMessage(
        "Application checked successfully"
      )
    );
  } catch (error: any) {
    yield put(manageApplicationSliceAction.setCheckApplicationID(null));
    yield put(manageApplicationSliceAction.setCheckApplicationStatus(false));
    yield put(
      manageApplicationSliceAction.setCheckApplicationMessage(
        error?.response?.data?.message ||
          "An error occurred while checking application"
      )
    );
  } finally {
    yield put(manageApplicationSliceAction.setCheckApplicationLoading(false));
  }
}

function* deleteApplicationIDSaga(action: {
  type: string;
  payload: DeleteApplicationPayload;
}) {
  try {
    yield put(manageApplicationSliceAction.setDeleteApplicationLoading(true));
    yield put(manageApplicationSliceAction.setDeleteApplicationStatus(false));
    yield put(manageApplicationSliceAction.setDeleteApplicationMessage(""));

    const response: DeleteApplicationResponse = yield call(
      deleteApplicationIDAPI,
      action.payload
    );
    yield put(manageApplicationSliceAction.setDeleteApplicationStatus(true));
    yield put(
      manageApplicationSliceAction.setDeleteApplicationMessage(response.message)
    );
    yield put(getOnboardingDetailListSagaAction(action.payload.partner_id));
  } catch (error: any) {
    yield put(manageApplicationSliceAction.setDeleteApplicationStatus(false));
    yield put(
      manageApplicationSliceAction.setDeleteApplicationMessage(
        error?.response?.data?.message ||
          "An error occurred while deleting application"
      )
    );
  } finally {
    yield put(manageApplicationSliceAction.setDeleteApplicationLoading(false));
  }
}

function* manageApplicationWatcher() {
  yield takeLatest(
    manageApplicationSagaActionTypes.callManageApplicationSaga,
    manageApplicationSaga
  );
  yield takeLatest(
    manageApplicationSagaActionTypes.callCheckApplicationIDSaga,
    checkApplicationIDSaga
  );
  yield takeLatest(
    manageApplicationSagaActionTypes.callDeleteApplicationIDSaga,
    deleteApplicationIDSaga
  );
}

export default manageApplicationWatcher;
